import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { InfiniteData } from 'react-query';
import { useCategoryQuery } from '../../../../api/category/queries';
import useOnUpdate from '../../../../hooks/useOnUpdate';
import useHistory from '../../../../Router/useHistory';
import { fromQueryStringObject, toQueryStringObject } from '../../../../utils/url';
import useTrackFilters from './useTrackFilters';
import useTrackSorting from './useTrackSorting';
import { cloneDeep } from 'lodash';

interface ICategoryContext {
  totalHits: number;
  disableLoadMore: boolean;
  errors: string[];
  availableFilters: IAvailableFilter[];
  filters: IFilters;
  setFilters: React.Dispatch<React.SetStateAction<IFilters>>;
  categoryChangedToggle: boolean;
  loadMore: () => void;
  loading: boolean;
  products: IProduct[];
  resetFilters: () => void;
  showCount: number;
  sort: (value: Sorting) => void;
  sorting: Sorting;
  updateStoreParam: (value: string | undefined) => void;
}

export const CategoryContext = createContext<ICategoryContext | undefined>(undefined);

const buildFilterQuery = (filters: IFilterValues) =>
  (filters &&
    Object.keys(filters)
      .sort()
      .reduce((params, property) => {
        const value = filters[property];
        if ((value as Record<string, boolean>).selected) {
          params.push(`${property}:(true)`);
        } else if (value.min === undefined || value.min === null) {
          params.push(
            `${property}:(${Object.keys(value)
              .map((v) => `"${encodeURIComponent(v)}"`)
              .join(' OR ')})`,
          );
        } else {
          params.push(`${property}:[${value.min} TO ${value.max}]`);
        }
        return params;
      }, [] as string[])
      .join(' AND ')) ||
  '';

const filtersEquals = (a?: IFilterValues, b?: IFilterValues) => {
  if (!a && !b) {
    return true;
  }

  if ((a && !b) || (b && !a)) {
    return false;
  }

  const keysA = !a ? [] : Object.keys(a).sort();
  const keysB = !b ? [] : Object.keys(b).sort();

  if (keysA.length !== keysB.length) {
    return false;
  }

  if (keysA.find((k, i) => k !== keysB[i])) {
    return false;
  }

  return !keysA.find((k) => {
    const valuesA = Object.keys(a?.[k] || {}).map((kk) => kk);
    const valuesB = Object.keys(b?.[k] || {}).map((kk) => kk);

    return valuesA.find((v, i) => v !== valuesB[i] || valuesB.find((v, i) => v !== valuesA[i]));
  });
};

const flattenProducts = (data?: InfiniteData<ICategory | undefined>) =>
  data?.pages?.reduce((products, page) => products.concat(page?.products || []), [] as IProduct[]);

const emptyFilters = {
  regular: {},
  leadtime: {},
};

interface ICategoryProvider extends ICategory {
  baseQuery?: string;
  children: ReactNode;
  totalHits: number;
  filters?: IFilters;
  id?: string;
  sort: Sorting;
  query?: string;
  store?: string;
}

const CategoryProvider = ({
  baseQuery: initialBaseQuery,
  availableFilters: initalAvailableFilters,
  children,
  totalHits,
  errors: initialErrors,
  filters: initialFilters = emptyFilters,
  store: initialStore,
  id: initialId,
  nextPage: initialToken,
  products: initialProducts,
  sort: initialSorting,
  query: initialQuery,
}: ICategoryProvider) => {
  const { replace } = useHistory();
  const [id, setId] = useState(initialId);
  const [baseQuery, setBaseQuery] = useState(initialBaseQuery);
  const [query, setQuery] = useState(initialQuery);
  const [filters, setFilters] = useState<IFilters>(cloneDeep(initialFilters));
  const [filterQuery, setFilterQuery] = useState(buildFilterQuery(filters.regular));
  const [deliveryQuery, setDeliveryQuery] = useState(buildFilterQuery(filters.leadtime));
  const [categoryChangedToggle, setCategoryChangedToggle] = useState(false);
  const [storeParam, setStoreParam] = useState(initialStore);
  const [sorting, setSorting] = useState<Sorting>(initialSorting || '');
  const [token, setToken] = useState<undefined | number>();
  const [hits, setHits] = useState(totalHits);
  useTrackFilters(filters);
  useTrackSorting(sorting);
  const [errors, setErrors] = useState<string[]>(Object.keys(initialErrors || {}));

  const isInitialFilters = useMemo(
    () =>
      filtersEquals(filters.regular, initialFilters.regular) &&
      filtersEquals(filters.leadtime, initialFilters.leadtime),
    [filters, initialFilters],
  );

  const [initialData, setInitialData] = useState({
    availableFilters: initalAvailableFilters,
    errors: initialErrors,
    products: initialProducts,
    nextPage: initialToken,
    totalHits,
  });

  const isInitialData =
    id === initialId &&
    isInitialFilters &&
    (storeParam || '') === (initialStore || '') &&
    (sorting || '') === (initialSorting || '');

  // navigation from category page to antoher category page means new circumstances -> reset everything
  // same with page with productlistingslice to another page with that slice
  useOnUpdate(
    () => {
      const isNewCategory = id !== initialId;
      const isNewProductListingSlice = baseQuery !== initialBaseQuery;
      const isNewSearch = query !== initialQuery;

      if (isNewCategory || isNewProductListingSlice || isNewSearch) {
        setHits(totalHits);
        setId(initialId);
        setBaseQuery(initialBaseQuery);
        setCategoryChangedToggle((prev) => !prev);
        setQuery(initialQuery);
        setToken(undefined);
        setSorting(initialSorting);
        setFilters(initialFilters);
        setStoreParam(initialStore);
        setDeliveryQuery(buildFilterQuery(initialFilters.leadtime));
        setFilterQuery(buildFilterQuery(initialFilters.regular));
        setInitialData({
          availableFilters: initalAvailableFilters,
          errors: initialErrors,
          products: initialProducts,
          nextPage: initialToken,
          totalHits,
        });
      }
    },
    undefined,
    [
      id,
      totalHits,
      initialBaseQuery,
      initialErrors,
      initialFilters,
      initialId,
      initialQuery,
      initialSorting,
      initialStore,
      initialToken,
    ],
  );

  const { data, fetchNextPage, isFetching, isFetchingNextPage } = useCategoryQuery(
    id,
    isInitialData ? initialData : undefined,
    token,
    filterQuery,
    deliveryQuery,
    storeParam,
    sorting,
    baseQuery,
    query,
  );

  const [products, setProducts] = useState<IProduct[]>(
    () => (data && flattenProducts(data)) || initialProducts,
  );

  const [availableFilters, setAvailableFilters] = useState<IAvailableFilter[]>(
    data?.pages?.[data?.pages?.length - 1]?.availableFilters || initalAvailableFilters,
  );

  const loading = isFetching || isFetchingNextPage;
  const lastToken = data?.pages?.[data?.pages?.length - 1]?.nextPage;
  const disableLoadMore = (!!data && !lastToken) || (!data && !initialToken);
  const lastErrors = data?.pages?.[data?.pages?.length - 1]?.errors;

  useEffect(() => {
    if (lastErrors && Object.keys(lastErrors)) {
      setErrors(Object.keys(lastErrors));
    }
  }, [lastErrors]);

  useEffect(() => {
    if (data) {
      setProducts(flattenProducts(data) || []);
      setAvailableFilters(
        data?.pages?.[data?.pages?.length - 1]?.availableFilters || initalAvailableFilters,
      );
      setHits(data.pages[data.pages.length - 1].totalHits);
    }
  }, [data]);

  const sort = useCallback((value: Sorting) => {
    setSorting(value);
  }, []);

  const updateStoreParam = useCallback((value: string | undefined) => {
    setStoreParam(value);
  }, []);

  useOnUpdate(
    () => {
      const regularFilterQuery = buildFilterQuery(filters.regular);
      const deliveryFilterQuery = buildFilterQuery(filters.leadtime);

      setFilterQuery(regularFilterQuery);
      setDeliveryQuery(deliveryFilterQuery);

      const queryString = toQueryStringObject(window.location.search);

      if (deliveryFilterQuery) {
        queryString.deliveryQuery = [deliveryFilterQuery];
      } else {
        delete queryString.deliveryQuery;
      }

      if (regularFilterQuery) {
        queryString.filter = [regularFilterQuery];
      } else {
        delete queryString.filter;
      }

      if (sorting) {
        queryString.sort = [sorting];
      } else {
        delete queryString.sort;
      }

      if (storeParam) {
        queryString.store = [storeParam];
      } else {
        delete queryString.store;
      }

      const search = fromQueryStringObject(queryString);

      if (search !== window.location.search) {
        replace({ search });
      }
    },
    undefined,
    [filters, sorting, storeParam],
  );

  const resetFilters = useCallback(() => {
    setFilters(emptyFilters);
  }, []);

  const context = useMemo(
    () => ({
      availableFilters,
      totalHits: hits,
      disableLoadMore,
      errors,
      filters,
      setFilters,
      loadMore: fetchNextPage,
      loading,
      products,
      resetFilters,
      showCount: lastToken || hits,
      sort,
      sorting,
      updateStoreParam,
      categoryChangedToggle,
    }),
    [
      disableLoadMore,
      errors,
      filters,
      setFilters,
      hits,
      products,
      availableFilters,
      loading,
      resetFilters,
      lastToken,
      updateStoreParam,
      categoryChangedToggle,
    ],
  );

  return <CategoryContext.Provider value={context}>{children}</CategoryContext.Provider>;
};

export default CategoryProvider;
